import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type TaskAppealRow } from '../lib/api';

const API_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Re-review queue (пересмотр). One component, two deciders:
 *   creator — round 1 of their own tasks ("первый пересмотр решает заказчик")
 *   staff   — round 2, and round 1 of tasks staff created. Only staff can take
 *             away an executor's right to appeal, and only on a final reject.
 */
export function AppealList({ mode, onChanged }: { mode: 'creator' | 'staff'; onChanged?: () => void }) {
    const [items, setItems] = useState<TaskAppealRow[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState<number | null>(null);
    const [rejectFor, setRejectFor] = useState<number | null>(null);
    const [reason, setReason] = useState('');
    const [revoke, setRevoke] = useState(false);

    const load = useCallback(async () => {
        setErr(null);
        try {
            const r = mode === 'creator' ? await api.incomingAppeals() : await api.adminListAppeals();
            setItems(r.items);
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
            setItems([]);
        }
    }, [mode]);
    useEffect(() => { void load(); }, [load]);

    const decide = async (a: TaskAppealRow, decision: 'accept' | 'reject') => {
        setBusy(a.id); setErr(null);
        try {
            await api.decideAppeal(a.id, decision === 'accept'
                ? { decision }
                : { decision, comment: reason.trim(), revokeAppeals: mode === 'staff' && a.round === 2 && revoke ? true : undefined });
            setRejectFor(null); setReason(''); setRevoke(false);
            await load();
            onChanged?.();
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.status}: ${e.code}` : String(e));
        } finally { setBusy(null); }
    };

    if (items === null) return <p className="text-muted text-center" style={{ padding: 24 }}>Загружаем…</p>;

    return (
        <div className="admin-list">
            {err && <div className="action-result err">{err}</div>}
            {items.length === 0 && (
                <p className="text-muted text-center" style={{ padding: 24 }}>Пересмотров на рассмотрении нет.</p>
            )}
            {items.map((a) => {
                const who = a.user.username ? `@${a.user.username}` : a.user.firstName ?? `#${a.user.id}`;
                const isBusy = busy === a.id;
                return (
                    <div key={a.id} className="admin-row" data-appeal-id={a.id}>
                        <div className="admin-row-top">
                            <span className="admin-row-handle">{a.task.title}</span>
                            <span className="admin-row-amount">{a.task.reward} BSP</span>
                        </div>
                        <div className="admin-row-meta">
                            <span className="admin-method">{who}</span>
                            <span className="text-muted f-12">
                                {a.round === 2 ? 'Повторный пересмотр' : 'Пересмотр'} · {new Date(a.createdAt).toLocaleString('ru-RU')}
                            </span>
                        </div>

                        <div style={{ marginTop: 8, fontSize: 12, display: 'grid', gap: 6 }}>
                            {a.submission.moderatorComment && (
                                <div className="task-card-note">Причина отказа: {a.submission.moderatorComment}</div>
                            )}
                            {a.task.checkType === 'word' && (
                                <div>
                                    <b>Введено:</b> {a.submission.responseText ?? '—'}
                                    {a.task.controlWord && <> · <b>Правильное слово:</b> {a.task.controlWord}</>}
                                </div>
                            )}
                            <div><b>Комментарий исполнителя:</b> {a.comment}</div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {a.submission.screenshotPath && (
                                    <figure style={{ margin: 0, flex: '1 1 140px' }}>
                                        <figcaption className="text-muted f-12">Первый скриншот</figcaption>
                                        <img src={`${API_URL}/api/uploads/${a.submission.screenshotPath}`} alt="Первый скриншот"
                                            style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8 }} />
                                    </figure>
                                )}
                                {a.screenshotPath && (
                                    <figure style={{ margin: 0, flex: '1 1 140px' }}>
                                        <figcaption className="text-muted f-12">Новый скриншот</figcaption>
                                        <img src={`${API_URL}/api/uploads/${a.screenshotPath}`} alt="Новый скриншот"
                                            style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8 }} />
                                    </figure>
                                )}
                            </div>
                        </div>

                        {rejectFor !== a.id ? (
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                <button type="button" className="action-btn btn-gold" disabled={isBusy} style={{ flex: 1 }}
                                    onClick={() => decide(a, 'accept')}>
                                    {isBusy ? '…' : '✓ Принять'}
                                </button>
                                <button type="button" className="action-btn btn-red" disabled={isBusy} style={{ flex: 1 }}
                                    onClick={() => { setRejectFor(a.id); setReason(''); setRevoke(false); }}>
                                    Отклонить
                                </button>
                            </div>
                        ) : (
                            <div style={{ marginTop: 8 }}>
                                <div className="input-group">
                                    <label>Причина отказа (увидит исполнитель)</label>
                                    <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
                                        placeholder="Например: на скриншоте не тот пост" />
                                </div>
                                {mode === 'staff' && a.round === 2 && (
                                    <label className="checkbox-row" style={{ justifyContent: 'flex-start', gap: 10, marginTop: 6 }}>
                                        <input type="checkbox" checked={revoke} onChange={(e) => setRevoke(e.target.checked)} />
                                        <span className="f-12" style={{ color: '#ff8585' }}>Лишить права на пересмотр (злоупотребление)</span>
                                    </label>
                                )}
                                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                    <button type="button" className="action-btn btn-mute" disabled={isBusy} style={{ flex: 1 }}
                                        onClick={() => setRejectFor(null)}>Назад</button>
                                    <button type="button" className="action-btn btn-red" disabled={isBusy || reason.trim().length === 0}
                                        style={{ flex: 1 }} onClick={() => decide(a, 'reject')}>
                                        {isBusy ? '…' : 'Отклонить'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
